import { db } from "@/repositories/database";
import type { DrawingImage, DRImageRecord } from "@/types";
import type { RepositoryBaseInterface } from "@/repositories/repository.interface";
import { BaseRepository } from "@/repositories/base.repository";
import { localDrawingRepository } from "./local-drawing.repository";
class DrawingImageRepository
  extends BaseRepository<DRImageRecord, number>
  implements RepositoryBaseInterface<DRImageRecord>
{
  constructor() {
    super(db.drawingImages, "drawingImage");
  }

  async getByDrawingId(drawingId: number): Promise<DRImageRecord[]> {
    return db.drawingImages.where("drawingId").equals(drawingId).toArray();
  }

  /**
   * `drawingId` distincts ayant au moins une image, lus DANS L'INDEX : aucune ligne
   * n'est désérialisée, donc aucun octet d'image ne transite. Indispensable pour le
   * nettoyage périodique (`Syncer.removeUnusedImagesInDb`, toutes les 10 s), qui ne
   * compare que des ids : y passer par `fetchAll()` chargeait les pixels de toutes
   * les images locales pour lire un entier par enregistrement.
   */
  async getDrawingIdsWithImages(): Promise<number[]> {
    const keys = await db.drawingImages.orderBy("drawingId").uniqueKeys();
    return keys as number[];
  }

  /**
   * Supprime toutes les images des dessins donnés, par clés (`primaryKeys` puis
   * `bulkDelete`) : là encore, aucune valeur n'est lue. Une seule transaction, au
   * lieu d'un `deleteById` par image.
   */
  async deleteByDrawingIds(drawingIds: number[]): Promise<void> {
    if (!drawingIds.length) return;
    const ids = await db.drawingImages
      .where("drawingId")
      .anyOf(drawingIds)
      .primaryKeys();
    if (!ids.length) return;
    await db.drawingImages.bulkDelete(ids);
  }

  async upsert(record: DRImageRecord): Promise<void> {
    await db.drawingImages.put(record);
  }

  async deleteByDrawingId(drawingId: number): Promise<void> {
    await db.drawingImages.where("drawingId").equals(drawingId).delete();
  }

  async deleteById(id: number): Promise<void> {
    await db.drawingImages.delete(id);
  }

  async appendNewImageOnDrawing(
    drawingId: number,
    rasterPath: string,
    data: Uint8Array<ArrayBuffer>,
    file: File
  ): Promise<void> {
    const tempId = Date.now() * -1;

    localDrawingRepository.addRasterPathOnLastRevision(drawingId, rasterPath);

    const record: DRImageRecord = {
      id: tempId,
      rasterPath,
      drawingId,
      data,
      originalName: file.name,
      mimeType: file.type,
      size: file.size,
    };

    await this.upsert(record);
  }

  async replaceNewRecordWithServerImage(
    newRecord: DRImageRecord,
    serverImage: DrawingImage
  ): Promise<void> {
    if (newRecord.rasterPath !== serverImage.rasterPath) return;
    await db.transaction("rw", db.drawingImages, async () => {
      await this.createSafeById({
        ...newRecord,
        id: serverImage.id,
        drawingId: serverImage.drawingId,
      });
      await this.deleteById(newRecord.id);
    });
  }
}

export const drawingImageRepository = new DrawingImageRepository();
